import type { Teacher } from '@kimuno/api-sdk/lib/types/responses/Teacher';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
	Box,
	CircularProgress,
	Divider,
	IconButton,
	Menu,
	MenuItem,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
	assignSchoolAdmin,
	removeSchoolTeacher,
	removeTeacherFromStore,
} from '../../../../actions/user.actions';
import { AdminRoleGranted, TeacherRemoved } from '../../../../ampli';
import { getProductByLicenceModelId, schoolLicences } from '../../../../constants';
import { selectSelectedSchoolId, selectUser } from '../../../../reducers/user.selectors';
import services from '../../../../services';
import { SnackbarContext } from '../../../../state/SnackbarContextProvider';
import useAction, { useNonThunkAction } from '../../../../utils/useAction';
import ConfirmDialog from '../../../Dialog/ConfirmationDialog';
import UpdateSchoolTeacher from '../UpdateSchoolTeacher/UpdateTeacher';

export enum ResendActionType {
	RESEND_INVITATION = 'RESEND_INVITATION',
	RESET_PASSWORD = 'RESET_PASSWORD',
}

interface TeacherItemProps {
	teacher: Teacher;
	index: number;
	onMenuClick: (event: React.MouseEvent<HTMLElement>, rowIndex: number) => void;
	onActionsMenuItemClick: (licenceModelId: number, teacher: Teacher) => void;
	onResendMenuItemClick: (actionType: ResendActionType, teacher: Teacher) => void;
	onAssignSchoolAdminClick: () => void;
	anchorEl: HTMLElement | null;
	menuOpen: boolean;
	onMenuClose: () => void;
	showChangeUserTypeButton: boolean;
	schoolSeats: any;
	onTeacherDeleted?: () => void;
	onTeacherUpdated?: () => void;
}

const MenuSection = (title: string): JSX.Element[] => [
	<Box key="section-header" sx={{ px: 2, py: 1 }}>
		<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
			{title}
		</Typography>
	</Box>,
	<Divider key="section-divider" />,
];

const TeacherItem: React.FC<TeacherItemProps> = ({
	teacher,
	index,
	onMenuClick,
	onActionsMenuItemClick,
	onResendMenuItemClick,
	onAssignSchoolAdminClick,
	anchorEl,
	menuOpen,
	onMenuClose,
	showChangeUserTypeButton,
	schoolSeats,
	onTeacherUpdated,
}) => {
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [assignAdminDialogOpen, setAssignAdminDialogOpen] = useState(false);
	const [updateTeacherDialogOpen, setUpdateTeacherDialogOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isAssigningAdmin, setIsAssigningAdmin] = useState(false);
	const removeSchoolTeacherAction = useAction(removeSchoolTeacher);
	const assignSchoolAdminAction = useAction(assignSchoolAdmin);
	const removeTeacherFromStoreAction = useNonThunkAction(removeTeacherFromStore);
	const { t } = useTranslation();
	const { openSnackbar } = useContext(SnackbarContext);
	const user = useSelector(selectUser);
	const selectedSchoolId = useSelector(selectSelectedSchoolId);

	let isActive: boolean;
	const sub = teacher.subscriptions?.[0];
	const isSchoolAdmin: boolean = teacher.isSchoolAdmin;
	// Check if the current user is the teacher
	if (user?.id === teacher.userId) {
		isActive = true;
	} else {
		isActive = sub?.status === 'Active';
	}

	const statusLabel = isActive ? t('Active') : t('Not active');
	const statusColor = isActive ? 'success.main' : 'error.main';
	const subscriptionType = sub?.subscriptionType || '-';
	const showChangeType = showChangeUserTypeButton && isActive;
	const currentLicenceModelId = sub?.licenceModelId ? Number(sub.licenceModelId) : undefined;
	const currentLicenceModel =
		(currentLicenceModelId
			? schoolLicences.models.find((model) => model.id === currentLicenceModelId)
			: undefined) ?? schoolLicences.models.find((model) => model.fullName === sub?.subscriptionType);

	const showError = (message: string) => {
		openSnackbar({
			type: 'error',
			message,
		});
	};

	const showInfo = (message: string) => {
		openSnackbar({
			type: 'success',
			message,
		});
	};

	const handleDeleteClick = () => {
		setConfirmDialogOpen(true);
	};

	const handleConfirmDialogClose = () => {
		setConfirmDialogOpen(false);
	};

	const handleConfirmDelete = async () => {
		if (isDeleting) {
			return; // Prevent multiple delete operations
		}

		if (!selectedSchoolId) {
			showError(t('No school selected'));
			return;
		}

		setIsDeleting(true);
		try {
			const result = await removeSchoolTeacherAction(teacher.userId, selectedSchoolId);
			if (!result.error) {
				// Remove teacher from Redux store immediately
				removeTeacherFromStoreAction(teacher.userId);
				showInfo(t('Teacher removed successfully'));
				handleConfirmDialogClose();

				const subscriptionType = teacher?.subscriptions?.[0]?.subscriptionType;
				if (
					user?.isSchoolAdmin &&
					selectedSchoolId &&
					teacher.userId &&
					schoolSeats &&
					subscriptionType
				) {
					const licenceModelId = teacher?.subscriptions?.[0]?.licenceModelId;
					const currentSubscription = licenceModelId
						? getProductByLicenceModelId(+licenceModelId)?.ampliName
						: null;

					if (currentSubscription) {
						services.track.ampliEventTrack({
							event: new TeacherRemoved({
								'school id': selectedSchoolId,
								'teacher user id': teacher.userId,
								'subscription products': [currentSubscription],
							}),
						});
					}
				}
			} else {
				let message: string;
				if (result.error.data.message.indexOf('Teacher account does not belong to school') > -1)
					message = t('Teacher account does not belong to school <schoolId>', {
						schoolId: result.error.data.message.replace('Teacher account does not belong to school ', ''),
					});
				else message = t(result.error.data.message) || t('Error while removing the teacher');
				showError(message);
				return;
			}
		} catch (err: any) {
			showError(t(err.message) || t('Error while removing the teacher'));
		} finally {
			setIsDeleting(false);
		}
	};

	const handleAssignAdminClick = () => {
		setAssignAdminDialogOpen(true);
		onMenuClose();
	};

	const handleAssignAdminDialogClose = () => {
		setAssignAdminDialogOpen(false);
	};

	const handleUpdateTeacherClick = () => {
		setUpdateTeacherDialogOpen(true);
		onMenuClose();
	};

	const handleUpdateTeacherDialogClose = () => {
		setUpdateTeacherDialogOpen(false);
	};

	const handleTeacherUpdated = () => {
		if (onTeacherUpdated) {
			onTeacherUpdated();
		}
	};

	const handleConfirmAssignAdmin = async () => {
		if (isAssigningAdmin) {
			return; // Prevent multiple assign operations
		}

		if (!selectedSchoolId) {
			showError(t('No school selected'));
			return;
		}

		setIsAssigningAdmin(true);
		try {
			const result = await assignSchoolAdminAction(teacher.userId, selectedSchoolId);
			onAssignSchoolAdminClick();
			if (!result.error) {
				showInfo(t('School admin assigned successfully'));

				if (user?.isSchoolAdmin && selectedSchoolId && teacher.userId) {
					services.track.ampliEventTrack({
						event: new AdminRoleGranted({
							'school id': selectedSchoolId,
							'teacher user id': teacher.userId,
						}),
					});
				}
			} else {
				showError(t(result.error.data.message));
			}
			handleAssignAdminDialogClose();
		} catch (err: any) {
			showError(t(err.message) || t('Error while assigning school admin'));
		} finally {
			setIsAssigningAdmin(false);
		}
	};

	const muProModel = schoolLicences.models.find((m) => m.id === schoolLicences.MU_PRO);
	const courseModel = schoolLicences.models.find((m) => m.id === schoolLicences.COURSE);
	const kombiModel = schoolLicences.models.find((m) => m.id === schoolLicences.KOMBI);

	const renderSubscriptionOptions = (): JSX.Element[] | null => {
		if (!showChangeType) return null;

		const changeOptions: JSX.Element[] = [];

		// Handle different subscription types
		if (currentLicenceModel?.id === schoolLicences.KOMBI) {
			if (schoolSeats?.bibliothekSeats?.remaining > 0) {
				changeOptions.push(
					<MenuItem
						key="downgrade-mu-pro"
						onClick={() => onActionsMenuItemClick(muProModel?.id ?? 0, teacher)}
					>
						{t('Downgrade to '.concat(muProModel?.shortName ?? ''))}
					</MenuItem>
				);
			}
			if (schoolSeats?.courseSeats?.remaining > 0) {
				changeOptions.push(
					<MenuItem
						key="downgrade-sfx-mf"
						onClick={() => onActionsMenuItemClick(courseModel?.id ?? 0, teacher)}
					>
						{t('Downgrade to '.concat(courseModel?.shortName ?? ''))}
					</MenuItem>
				);
			}
		} else if (currentLicenceModel?.id === schoolLicences.MU_PRO) {
			if (schoolSeats?.kombiSeats?.remaining > 0) {
				changeOptions.push(
					<MenuItem
						key="upgrade-mu-360"
						onClick={() => onActionsMenuItemClick(kombiModel?.id ?? 0, teacher)}
					>
						{t('Upgrade to '.concat(kombiModel?.shortName ?? ''))}
					</MenuItem>
				);
			}
			if (schoolSeats?.courseSeats?.remaining > 0) {
				changeOptions.push(
					<MenuItem
						key="switch-sfx-mf"
						onClick={() => onActionsMenuItemClick(courseModel?.id ?? 0, teacher)}
					>
						{t('Switch to '.concat(courseModel?.shortName ?? ''))}
					</MenuItem>
				);
			}
		} else if (currentLicenceModel?.id === schoolLicences.COURSE) {
			if (schoolSeats?.kombiSeats?.remaining > 0) {
				changeOptions.push(
					<MenuItem
						key="upgrade-mu-360"
						onClick={() => onActionsMenuItemClick(kombiModel?.id ?? 0, teacher)}
					>
						{t('Upgrade to '.concat(kombiModel?.shortName ?? ''))}
					</MenuItem>
				);
			}
			if (schoolSeats?.bibliothekSeats?.remaining > 0) {
				changeOptions.push(
					<MenuItem
						key="switch-mu-pro"
						onClick={() => onActionsMenuItemClick(muProModel?.id ?? 0, teacher)}
					>
						{t('Switch to '.concat(muProModel?.shortName ?? ''))}
					</MenuItem>
				);
			}
		} else {
			if (schoolSeats?.bibliothekSeats?.remaining > 0) {
				changeOptions.push(
					<MenuItem key="mu-pro" onClick={() => onActionsMenuItemClick(muProModel?.id ?? 0, teacher)}>
						{muProModel?.shortName}
					</MenuItem>
				);
			}
			if (schoolSeats?.courseSeats?.remaining > 0) {
				changeOptions.push(
					<MenuItem key="sfx-mf" onClick={() => onActionsMenuItemClick(courseModel?.id ?? 0, teacher)}>
						{courseModel?.shortName}
					</MenuItem>
				);
			}
			if (schoolSeats?.kombiSeats?.remaining > 0) {
				changeOptions.push(
					<MenuItem key="mu-360" onClick={() => onActionsMenuItemClick(kombiModel?.id ?? 0, teacher)}>
						{kombiModel?.shortName}
					</MenuItem>
				);
			}
		}

		if (changeOptions.length === 0) return null;

		return [...MenuSection(t('Change subscription type')), ...changeOptions];
	};

	const renderResendOptions = (hasChangeSubscriptionOptions = false): JSX.Element[] => {
		const resendOption = !isActive ? (
			<MenuItem
				key="resend-invitation"
				onClick={() => onResendMenuItemClick(ResendActionType.RESEND_INVITATION, teacher)}
			>
				{t('Resend Invitation')}
			</MenuItem>
		) : (
			<MenuItem
				key="reset-password"
				onClick={() => onResendMenuItemClick(ResendActionType.RESET_PASSWORD, teacher)}
			>
				{t('Reset Password')}
			</MenuItem>
		);

		const options = [resendOption];

		// Always show the section header if there are change subscription options
		if (hasChangeSubscriptionOptions || options.length > 1) {
			return [...MenuSection(t('Additional actions')), ...options];
		}
		return options;
	};

	// In the render, determine if there are subscription options
	const subscriptionOptions = renderSubscriptionOptions();
	return (
		<>
			<TableRow sx={{ height: '73px' }}>
				<TableCell
					sx={{
						maxWidth: 260,
						overflowX: 'auto',
						whiteSpace: 'nowrap',
						p: 0,
					}}
				>
					<Box sx={{ display: 'block', minWidth: '100%', px: 2 }}>{teacher.name}</Box>
				</TableCell>
				<TableCell
					sx={{
						maxWidth: 430,
						p: 0,
					}}
				>
					<Box
						sx={{
							display: 'block',
							maxWidth: 400,
							px: 2,
							overflowX: 'auto',
							whiteSpace: 'nowrap',
						}}
					>
						{teacher.email}
					</Box>
				</TableCell>
				<TableCell sx={{ minWidth: 180 }}>
					<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
						<Typography color={statusColor} component="span">
							{statusLabel}
						</Typography>
						{isSchoolAdmin && (
							<Box
								component="span"
								sx={{
									display: 'inline-block',
									background: 'rgba(25, 118, 210, 0.08)',
									color: '#1976d2',
									borderRadius: '4px',
									fontSize: '0.75rem',
									fontWeight: 400,
									px: 1,
									py: 0.25,
									verticalAlign: 'middle',
									border: '1px solid rgba(25, 118, 210, 0.2)',
								}}
								data-testid="admin-label"
							>
								Admin
							</Box>
						)}
					</Box>
				</TableCell>
				<TableCell>
					{currentLicenceModel ? (
						<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
							<span>{currentLicenceModel.shortName}</span>
							<Tooltip title={currentLicenceModel.fullName} arrow>
								<InfoOutlinedIcon
									sx={{
										fontSize: 18,
										color: 'text.secondary',
										cursor: 'pointer',
									}}
								/>
							</Tooltip>
						</Box>
					) : (
						subscriptionType
					)}
				</TableCell>
				<TableCell>
					{/* It's a basis admin, we hide buttons for him right now */}
					{teacher.subscriptions?.length > 0 && (
						<>
							<IconButton
								aria-label="delete"
								sx={{ color: '#e57373', cursor: 'pointer' }}
								onClick={handleDeleteClick}
								disabled={isDeleting}
							>
								{isDeleting ? <CircularProgress size={20} sx={{ color: '#e57373' }} /> : <DeleteIcon />}
							</IconButton>
							<IconButton
								aria-label="more"
								aria-controls={menuOpen ? 'teacher-menu' : undefined}
								aria-haspopup="true"
								onClick={(e) => onMenuClick(e, index)}
								color="success"
								sx={{ cursor: 'pointer' }}
							>
								<MoreVertIcon />
							</IconButton>
							<Menu
								id="teacher-menu"
								anchorEl={anchorEl}
								keepMounted
								open={menuOpen}
								onClose={onMenuClose}
							>
								{subscriptionOptions}
								{renderResendOptions(!!subscriptionOptions)}
								<MenuItem key="update-teacher" onClick={handleUpdateTeacherClick}>
									{t('Update Teacher')}
								</MenuItem>
								{!teacher.isSchoolAdmin && (
									<MenuItem key="assign-school-admin" onClick={handleAssignAdminClick}>
										{t('Assign School Admin')}
									</MenuItem>
								)}
							</Menu>
						</>
					)}
				</TableCell>
			</TableRow>
			<ConfirmDialog
				isOpen={confirmDialogOpen}
				title={t('Delete Teacher')}
				message={t('Are you sure you want to delete <teachername> <email>?', {
					teachername: teacher.name,
					email: teacher.email,
				})}
				onConfirm={handleConfirmDelete}
				onCloseModal={handleConfirmDialogClose}
				confirmButtonText={isDeleting ? t('Deleting...') : t('Delete')}
				confirmButtonColor="error"
			/>
			<ConfirmDialog
				isOpen={assignAdminDialogOpen}
				title={t('Assign School Admin')}
				message={t('Are you sure you want to assign <teachername> <email> as school admin?', {
					teachername: teacher.name,
					email: teacher.email,
				})}
				onConfirm={handleConfirmAssignAdmin}
				onCloseModal={handleAssignAdminDialogClose}
				confirmButtonText={t('Yes')}
				confirmButtonColor="success"
			/>
			<UpdateSchoolTeacher
				teacher={teacher}
				open={updateTeacherDialogOpen}
				onClose={handleUpdateTeacherDialogClose}
				onTeacherUpdated={handleTeacherUpdated}
			/>
		</>
	);
};

export default TeacherItem;
